import * as Yup from 'yup';

import { AlertMessage, FieldError, Spinner } from '../../../../widgets';
import { Form, Formik, FormikHelpers } from 'formik';

import DateQuestion from './DateQuestion';
import DropdownQuestion from './DropdownQuestion';
import FileUploadQuestion from './FileUploadQuestion';
import { IEntity } from '../../../../base/types';
import { IFeedbackForm } from '../models/feedback-form.model';
import LikertQuestion from './LikertQuestion';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import NPSQuestion from './NPSQuestion';
import RankingQuestion from './RankingQuestion';
import RatingQuestion from './RatingQuestion';
import TextQuestion from './TextQuestion';
import TimeQuestion from './TimeQuestion';

const createFormikSchema = (feedbackForm: IFeedbackForm) => {
    const initialValues: IEntity = {};
    const schema: any = {};

    feedbackForm.questions.forEach((question) => {
        const fieldName = question.id;
        schema[fieldName] = question.isRequired ?
            Yup.string().required('This field is required').label(question.title) :
            Yup.string().label(question.title);
    });

    const validationSchema = Yup.object().shape(schema);
    return { initialValues, validationSchema };
}

const FeedbackForm = ({ form, onSubmit }: {
    form: IFeedbackForm,
    onSubmit: (values: IEntity) => Promise<void>
}) => {
    const { initialValues, validationSchema } = createFormikSchema(form);

    const submitStep = (values: IEntity, { setStatus, setSubmitting }: FormikHelpers<IEntity>) => {
        setSubmitting(true)
        setStatus()

        onSubmit(values)
            .catch((err) => setStatus(`${err}`))
            .finally(() => setSubmitting(false))
    }

    return (
        <Formik
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={submitStep}>
            {({ errors, touched, submitCount, isSubmitting, status, values, setFieldValue, setFieldTouched }) => (
                <Form noValidate className='card'>
                    <div className='card-header border-0 min-h-5px'>
                        <div>
                            <h1 className="mt-4 mb-0">{form.title}</h1>
                            <p className="fs-7 text-muted my-0">{form.description}</p>
                        </div>
                    </div>
                    <div className="card-body">
                        {form.questions.map((question, index) => (
                            <div className="mb-3" key={index}>
                                {question.type === 'text' && <TextQuestion
                                    value={values[question.id]}
                                    onChange={(value) => setFieldValue(question.id, value)}
                                    onBlur={() => setFieldTouched(question.id, true)}
                                    question={question} />}
                                {question.type === 'multipleChoice' && <MultipleChoiceQuestion
                                    value={values[question.id]}
                                    onChange={(value) => setFieldValue(question.id, value)}
                                    onBlur={() => setFieldTouched(question.id, true)}
                                    question={question} />}
                                {question.type === 'rating' && <RatingQuestion
                                    value={values[question.id]}
                                    onChange={(value) => setFieldValue(question.id, value)}
                                    onBlur={() => setFieldTouched(question.id, true)}
                                    question={question} />}
                                {question.type === 'date' && <DateQuestion
                                    value={values[question.id]}
                                    onChange={(value) => setFieldValue(question.id, value)}
                                    onBlur={() => setFieldTouched(question.id, true)}
                                    question={question}
                                />}
                                {question.type === 'time' && <TimeQuestion
                                    value={values[question.id]}
                                    onChange={(value) => setFieldValue(question.id, value)}
                                    onBlur={() => setFieldTouched(question.id, true)}
                                    question={question}
                                />}
                                {question.type === 'dropdown' && <DropdownQuestion
                                    value={values[question.id]}
                                    onChange={(value) => setFieldValue(question.id, value)}
                                    onBlur={() => setFieldTouched(question.id, true)}
                                    question={question} />}
                                {question.type === 'likert' && <LikertQuestion
                                    value={values[question.id]}
                                    onChange={(value) => setFieldValue(question.id, value)}
                                    onBlur={() => setFieldTouched(question.id, true)}
                                    question={question} />}
                                {question.type === 'ranking' && <RankingQuestion
                                    value={values[question.id]}
                                    onChange={(value) => setFieldValue(question.id, value)}
                                    onBlur={() => setFieldTouched(question.id, true)}
                                    question={question} />}
                                {question.type === 'fileUpload' && <FileUploadQuestion
                                    value={values[question.id]}
                                    onChange={(value) => setFieldValue(question.id, value)}
                                    onBlur={() => setFieldTouched(question.id, true)}
                                    question={question} />}
                                {question.type === 'NPS' && <NPSQuestion
                                    value={values[question.id]}
                                    onChange={(value) => setFieldValue(question.id, value)}
                                    onBlur={() => setFieldTouched(question.id, true)}
                                    question={question} />}
                                <FieldError show={(touched[question.id] === true || submitCount > 0) && !!errors[question.id]} message={errors[question.id] as string} />
                            </div>
                        ))}

                        <AlertMessage type='Error' show={!!status} message={status} />

                        <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                            <Spinner show={isSubmitting} />
                            Submit
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default FeedbackForm;
